import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const RESOLUTIONS = [
  { id: "omit", label: "Omit this component" },
  { id: "supplied", label: "I'll supply it myself" },
  { id: "paused", label: "Pause — brand will supply" },
];

/**
 * Step 1 of the workflow: list every slot the AI Assembly Agent could not
 * fully resolve. For each, the marketer picks a resolution; "Pause" creates
 * a Blocker for the brand administrator and moves the dreq into
 * `paused_for_brand`.
 */
export default function InputsStep({
  document,
  unresolvedRows,
  onChanged,
  onAdvance,
}) {
  // Local state of resolutions keyed by `${panel_id}::${slot_id}`.
  const seed = useMemo(() => {
    const map = {};
    for (const r of unresolvedRows) {
      const key = `${r.panel_id}::${r.slot_id}`;
      map[key] = { resolution: null, marketer_note: "" };
    }
    return map;
  }, [unresolvedRows]);
  const [state, setState] = useState(seed);
  const [pausedBlockers, setPausedBlockers] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Pull existing blockers so we can show "already paused" rows.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/document-versions/${document.document_version_id}/blockers`);
        if (cancelled) return;
        const map = {};
        for (const b of data) {
          if (b.status === "open") {
            map[`${b.panel_id}::${b.slot_id}`] = b;
          }
        }
        setPausedBlockers(map);
      } catch {
        // non-fatal — keep marketing flow working
      }
    })();
    return () => { cancelled = true; };
  }, [document.document_version_id]);

  const pick = (key, resolution) => {
    setState((s) => ({ ...s, [key]: { ...(s[key] || {}), resolution } }));
  };
  const note = (key, marketer_note) => {
    setState((s) => ({ ...s, [key]: { ...(s[key] || {}), marketer_note } }));
  };

  const allDecided = unresolvedRows.every((r) => {
    const k = `${r.panel_id}::${r.slot_id}`;
    return pausedBlockers[k] || (state[k] && state[k].resolution);
  });
  const anyPaused = unresolvedRows.some((r) => {
    const k = `${r.panel_id}::${r.slot_id}`;
    return pausedBlockers[k] || state[k]?.resolution === "paused";
  });

  const submit = async () => {
    setBusy(true); setError("");
    try {
      // Send Pause choices to backend (creates Blockers + flips dreq paused).
      for (const r of unresolvedRows) {
        const k = `${r.panel_id}::${r.slot_id}`;
        if (pausedBlockers[k]) continue; // already paused
        const choice = state[k];
        if (choice?.resolution === "paused") {
          await api.post(`/document-versions/${document.document_version_id}/blockers`, {
            panel_id: r.panel_id,
            slot_id: r.slot_id,
            slot_label: r.slot_label,
            kind: r.kind,
            subject_hint: r.subject_hint,
            what_the_ai_tried: r.what_the_ai_tried,
            marketer_note: (choice.marketer_note || "").trim() || null,
          });
        }
      }
      onChanged?.();
      // If marketer paused anything, the dreq is now paused — stay here.
      // Otherwise advance to Content.
      if (!anyPaused) onAdvance?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save resolutions");
    } finally {
      setBusy(false);
    }
  };

  if (unresolvedRows.length === 0) {
    return (
      <section data-testid="inputs-step" className="mt-8">
        <h2 className="font-serif text-2xl">All inputs satisfied</h2>
        <p className="text-sm text-[#3D3A35] mt-2 max-w-2xl">
          The AI Assembly Agent has every input it needs from your brand's
          known content. Move on to <strong>Content</strong> to review what
          it assembled.
        </p>
        <button
          data-testid="inputs-step-advance"
          onClick={onAdvance}
          className="mt-5 px-5 py-2.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium"
        >
          Continue to Content →
        </button>
      </section>
    );
  }

  return (
    <section data-testid="inputs-step" className="mt-8">
      <h2 className="font-serif text-2xl">Inputs we need</h2>
      <p className="text-sm text-[#3D3A35] mt-2 max-w-2xl">
        These components are required to fulfill your design request, but
        source material is not currently available from your brand's known
        content. Choose how you'd like to handle each one.
      </p>

      <div className="mt-6 space-y-3">
        {unresolvedRows.map((r) => {
          const key = `${r.panel_id}::${r.slot_id}`;
          const paused = pausedBlockers[key];
          const choice = state[key] || {};
          return (
            <div
              key={key}
              data-testid={`inputs-row-${r.panel_id}-${r.slot_id}`}
              className="border border-[#1A1F2C]/10 bg-white rounded-lg p-5"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">
                    {r.kind} · {r.panel_role || r.panel_id}
                  </div>
                  <h3 className="text-base font-medium text-[#1A1F2C] mt-0.5">
                    {r.slot_label || r.slot_id}
                  </h3>
                  {r.subject_hint && (
                    <p className="text-xs text-[#3D3A35] mt-1">Subject hint: <em>{r.subject_hint}</em></p>
                  )}
                  {r.what_the_ai_tried && (
                    <p className="text-xs text-[#6B6358] mt-1 max-w-2xl">
                      <strong>What the AI tried:</strong> {r.what_the_ai_tried}
                    </p>
                  )}
                </div>
                {paused && (
                  <span className="text-xs uppercase tracking-[0.18em] bg-[#C28A2E] text-[#F8F5EE] px-3 py-1 rounded-full">
                    Paused — waiting on brand
                  </span>
                )}
              </div>

              {!paused && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {RESOLUTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      data-testid={`inputs-row-${r.panel_id}-${r.slot_id}-${opt.id}`}
                      onClick={() => pick(key, opt.id)}
                      className={[
                        "px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] border",
                        choice.resolution === opt.id
                          ? "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]"
                          : "bg-white text-[#3D3A35] border-[#1A1F2C]/30",
                      ].join(" ")}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {!paused && choice.resolution === "paused" && (
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#6B6358] mb-1">
                    Note for brand administrator (optional)
                  </p>
                  <textarea
                    data-testid={`inputs-row-${r.panel_id}-${r.slot_id}-note`}
                    value={choice.marketer_note || ""}
                    onChange={(e) => note(key, e.target.value)}
                    rows={2}
                    placeholder="e.g. 'For our anniversary mailer; ideally multi-generational.'"
                    className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md text-sm bg-white"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <p className="text-sm text-[#B5462F] mt-4">{error}</p>}

      <div className="mt-6 flex items-center gap-3">
        <button
          data-testid="inputs-step-submit"
          disabled={!allDecided || busy}
          onClick={submit}
          className="px-5 py-2.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium disabled:opacity-40"
        >
          {busy
            ? "Saving…"
            : anyPaused
            ? "Save resolutions & pause the document"
            : "Continue to Content →"}
        </button>
        {!allDecided && (
          <span className="text-xs text-[#6B6358]">
            Choose a resolution for every component to continue.
          </span>
        )}
      </div>
    </section>
  );
}
